import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Switch } from '../components/ui/switch';
import { 
  Plus, Trash2, Edit, Mail, Target, AlertCircle, CheckCircle, 
  XCircle, Users, Building, ArrowRight, Eye, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';

export default function EmailRoutingSettings() {
  const [routingRules, setRoutingRules] = useState([]);
  const [domainMappings, setDomainMappings] = useState([]);
  const [boards, setBoards] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [editRuleDialogOpen, setEditRuleDialogOpen] = useState(false);
  const [viewMappingDialogOpen, setViewMappingDialogOpen] = useState(false);
  const [editMappingDialogOpen, setEditMappingDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  
  // Form data
  const [ruleFormData, setRuleFormData] = useState({
    name: '',
    rule_type: 'recipient_address',
    match_value: '',
    target_board_id: '',
    priority: 0,
    action: 'route'
  });
  
  const [editRuleFormData, setEditRuleFormData] = useState({
    name: '',
    match_value: '',
    target_board_id: '',
    is_active: true,
    priority: 0,
    action: 'route'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [rulesRes, mappingsRes, boardsRes, customersRes] = await Promise.all([
        api.get('/email-routing-rules').catch(() => ({ data: [] })),
        api.get('/domain-mappings').catch(() => ({ data: [] })),
        api.get('/ticket-boards').catch(() => ({ data: [] })),
        api.get('/customers', { params: { limit: 10000, status: 'active' } }).catch(() => ({ data: { customers: [] } }))
      ]);
      
      setRoutingRules(Array.isArray(rulesRes.data) ? rulesRes.data : []);
      setDomainMappings(Array.isArray(mappingsRes.data) ? mappingsRes.data : []);
      setBoards(Array.isArray(boardsRes.data) ? boardsRes.data : []);
      const cd = customersRes.data; setCustomers(Array.isArray(cd) ? cd : cd.customers || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load settings');
      // Ensure arrays are set even on error
      setRoutingRules([]);
      setDomainMappings([]);
      setBoards([]);
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRule = async (e) => {
    e.preventDefault();
    try {
      await api.post('/email-routing-rules', ruleFormData);
      toast.success('Routing rule created');
      setRuleDialogOpen(false);
      setRuleFormData({
        name: '',
        rule_type: 'recipient_address',
        match_value: '',
        target_board_id: '',
        priority: 0,
        action: 'route'
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create routing rule');
    }
  };

  const handleUpdateRule = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/email-routing-rules/${editingRule.id}`, editRuleFormData);
      toast.success('Routing rule updated');
      setEditRuleDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Failed to update routing rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this routing rule?')) return;
    
    try {
      await api.delete(`/email-routing-rules/${ruleId}`);
      toast.success('Routing rule deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete routing rule');
    }
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setEditRuleFormData({
      name: rule.name,
      match_value: rule.match_value,
      target_board_id: rule.target_board_id,
      is_active: rule.is_active,
      priority: rule.priority
    });
    setEditRuleDialogOpen(true);
  };

  const handleApproveMapping = async (mappingId) => {
    try {
      await api.put(`/domain-mappings/${mappingId}/approve`);
      toast.success('Domain mapping approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve mapping');
    }
  };

  const handleRejectMapping = async (mappingId) => {
    if (!window.confirm('Reject this domain mapping?')) return;
    
    try {
      await api.put(`/domain-mappings/${mappingId}/reject`);
      toast.success('Domain mapping rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject mapping');
    }
  };

  const pendingMappings = domainMappings.filter(m => m.status === 'pending');
  const approvedMappings = domainMappings.filter(m => m.status === 'approved');

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex items-center justify-center min-h-[60vh]">
        <div className="text-gray-500">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-6" data-testid="email-routing-settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Email Routing & Domain Settings</h1>
        <p className="text-gray-600 mt-1">
          <strong>Routing rules</strong> decide which board a new email becomes a ticket on (or block it entirely).
          {' '}<strong>Domain mappings</strong> link every sender from a company domain to the right customer record.
        </p>
      </div>

      <Tabs defaultValue="routing" className="space-y-4">
        <TabsList>
          <TabsTrigger value="routing">
            <Target className="h-4 w-4 mr-2" />
            Routing Rules ({routingRules.length})
          </TabsTrigger>
          <TabsTrigger value="domains">
            <Building className="h-4 w-4 mr-2" />
            Domain Mappings
            {pendingMappings.length > 0 && (
              <Badge className="ml-2 bg-orange-100 text-orange-800">
                {pendingMappings.length} Pending
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* ROUTING RULES TAB */}
        <TabsContent value="routing">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Routing Rules</CardTitle>
                  <CardDescription>
                    Route incoming emails to specific ticket boards based on recipient address or keywords
                  </CardDescription>
                </div>
                <Dialog open={ruleDialogOpen} onOpenChange={setRuleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Routing Rule
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="co-scope co-settings max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Create Routing Rule</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreateRule} className="space-y-4">
                      <div className="space-y-2">
                        <Label>Rule Name *</Label>
                        <Input
                          value={ruleFormData.name}
                          onChange={(e) => setRuleFormData({...ruleFormData, name: e.target.value})}
                          required
                          placeholder="e.g., Billing Emails"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Action *</Label>
                        <Select
                          value={ruleFormData.action || 'route'}
                          onValueChange={(value) => {
                            const newData = {...ruleFormData, action: value};
                            if (value === 'block') {
                              // When blocking, we need to determine if it's email or domain
                              if (ruleFormData.rule_type === 'recipient_address' || ruleFormData.rule_type === 'keyword') {
                                newData.rule_type = 'block_email';
                              }
                              newData.target_board_id = '';
                            }
                            setRuleFormData(newData);
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="route">Route to Board</SelectItem>
                            <SelectItem value="block">Block Email</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          {ruleFormData.action === 'block' 
                            ? 'Prevent ticket creation from matching emails' 
                            : 'Route matching emails to a specific board'}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Rule Type *</Label>
                        <Select
                          value={ruleFormData.rule_type}
                          onValueChange={(value) => setRuleFormData({...ruleFormData, rule_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ruleFormData.action === 'route' ? (
                              <>
                                <SelectItem value="recipient_address">Recipient Address</SelectItem>
                                <SelectItem value="keyword">Keyword in Subject/Body</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="block_email">Block Specific Email</SelectItem>
                                <SelectItem value="block_domain">Block Entire Domain</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          {ruleFormData.rule_type === 'recipient_address' ? 'Match emails sent to a specific address' :
                           ruleFormData.rule_type === 'keyword' ? 'Match emails containing specific keywords' :
                           ruleFormData.rule_type === 'block_email' ? 'Block specific email address from creating tickets' :
                           'Block entire domain from creating tickets'}
                        </p>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Match Value *</Label>
                        <Input
                          value={ruleFormData.match_value}
                          onChange={(e) => setRuleFormData({...ruleFormData, match_value: e.target.value})}
                          required
                          placeholder={
                            ruleFormData.rule_type === 'recipient_address' ? 'billing@mathewsit.com' :
                            ruleFormData.rule_type === 'keyword' ? 'invoice, payment, billing' :
                            ruleFormData.rule_type === 'block_email' ? 'spam@example.com' :
                            'spammer.com'
                          }
                        />
                        <p className="text-xs text-gray-500">
                          {ruleFormData.rule_type === 'recipient_address' ? 'Enter the email address to match' :
                           ruleFormData.rule_type === 'keyword' ? 'Enter keywords to match (case-insensitive)' :
                           ruleFormData.rule_type === 'block_email' ? 'Enter email address to block' :
                           'Enter domain to block (e.g., spammer.com)'}
                        </p>
                      </div>
                      
                      {ruleFormData.action === 'route' && (
                        <div className="space-y-2">
                          <Label>Target Board *</Label>
                          <Select
                            value={ruleFormData.target_board_id}
                            onValueChange={(value) => setRuleFormData({...ruleFormData, target_board_id: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a board" />
                            </SelectTrigger>
                            <SelectContent>
                              {boards.filter(b => b.is_active).map((board) => (
                                <SelectItem key={board.id} value={board.id}>
                                  {board.name}
                                  {board.is_default && ' (Default)'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {ruleFormData.action === 'block' && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <XCircle className="h-5 w-5 text-red-700 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-900">
                              <strong>Blocking rule.</strong> Emails matching this rule will not create tickets
                              or customers. They are still imported to your inbox so you can review them.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label>Priority</Label>
                        <Input
                          type="number"
                          min="0"
                          value={ruleFormData.priority}
                          onChange={(e) => setRuleFormData({...ruleFormData, priority: parseInt(e.target.value)})}
                          placeholder="0"
                        />
                        <p className="text-xs text-gray-500">Lower number = higher priority (checked first)</p>
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setRuleDialogOpen(false)}>
                          Cancel
                        </Button>
                        <Button type="submit">
                          Create Rule
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {routingRules.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Target className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold mb-2">No Routing Rules</p>
                  <p className="text-sm">Create rules to automatically route emails to specific boards</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Priority</TableHead>
                      <TableHead>Rule Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Match Value</TableHead>
                      <TableHead>Target Board</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {routingRules.map((rule) => (
                      <TableRow key={rule.id}>
                        <TableCell>
                          <Badge variant="outline" className="font-mono">
                            {rule.priority}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-semibold">{rule.name}</TableCell>
                        <TableCell>
                          <Badge className={
                            rule.rule_type === 'recipient_address' ? 'bg-blue-100 text-blue-800' :
                            rule.rule_type === 'keyword' ? 'bg-purple-100 text-purple-800' :
                            rule.rule_type === 'block_email' ? 'bg-red-100 text-red-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {rule.rule_type === 'recipient_address' ? (
                              <><Mail className="h-3 w-3 mr-1 inline" />Recipient</>
                            ) : rule.rule_type === 'keyword' ? (
                              <><Target className="h-3 w-3 mr-1 inline" />Keyword</>
                            ) : rule.rule_type === 'block_email' ? (
                              <><XCircle className="h-3 w-3 mr-1 inline" />Block Email</>
                            ) : (
                              <><XCircle className="h-3 w-3 mr-1 inline" />Block Domain</>
                            )}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{rule.match_value}</TableCell>
                        <TableCell>
                          {rule.action === 'block' || !rule.target_board_name ? (
                            <Badge className="bg-red-100 text-red-800">
                              <XCircle className="h-3 w-3 mr-1 inline" />
                              Blocked
                            </Badge>
                          ) : (
                            <Badge variant="outline">{rule.target_board_name}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={rule.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleEditRule(rule)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteRule(rule.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {boards.filter(b => b.is_default).length === 0 && (
                <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-yellow-900">No Default Board Set</p>
                      <p className="text-sm text-yellow-700 mt-1">
                        Emails that don't match any rules won't be routed to a board. 
                        Go to Ticket Boards settings to mark a board as default.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* DOMAIN MAPPINGS TAB */}
        <TabsContent value="domains">
          {pendingMappings.length > 0 && (
            <Card className="border-orange-300 bg-orange-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-900">
                  <AlertCircle className="h-5 w-5" />
                  Pending Approval ({pendingMappings.length})
                </CardTitle>
                <CardDescription className="text-orange-700">
                  Review and approve domain-to-customer mappings created from incoming emails
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Proposed Customer</TableHead>
                      <TableHead>Sample Emails</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingMappings.map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-gray-500" />
                            <span className="font-mono font-semibold">{mapping.domain}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-blue-100 text-blue-800">
                            {mapping.customer_name}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            {mapping.sample_emails.slice(0, 2).map((email, idx) => (
                              <span key={idx} className="text-xs text-gray-600">{email}</span>
                            ))}
                            {mapping.sample_emails.length > 2 && (
                              <span className="text-xs text-gray-400">
                                +{mapping.sample_emails.length - 2} more
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatInCST(mapping.created_at, 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveMapping(mapping.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedMapping(mapping);
                                setViewMappingDialogOpen(true);
                              }}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              Review
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleRejectMapping(mapping.id)}
                              className="text-red-600"
                            >
                              <XCircle className="h-3 w-3 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Approved Domain Mappings</CardTitle>
              <CardDescription>
                Automatically link all emails from these domains to their assigned customers
              </CardDescription>
            </CardHeader>
            <CardContent>
              {approvedMappings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Building className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold mb-2">No Approved Mappings</p>
                  <p className="text-sm">Domain mappings will appear here after approval</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Emails Matched</TableHead>
                      <TableHead>Approved By</TableHead>
                      <TableHead>Approved Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {approvedMappings.map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building className="h-4 w-4 text-green-600" />
                            <span className="font-mono font-semibold">{mapping.domain}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <ArrowRight className="h-4 w-4 text-gray-400" />
                            <span className="font-semibold">{mapping.customer_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {mapping.sample_emails.length} email(s)
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {mapping.approved_by || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {mapping.approved_at ? formatInCST(mapping.approved_at, 'MMM d, yyyy') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Rule Dialog */}
      <Dialog open={editRuleDialogOpen} onOpenChange={setEditRuleDialogOpen}>
        <DialogContent className="co-scope co-settings max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Routing Rule</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRule} className="space-y-4">
            <div className="space-y-2">
              <Label>Rule Name *</Label>
              <Input
                value={editRuleFormData.name}
                onChange={(e) => setEditRuleFormData({...editRuleFormData, name: e.target.value})}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Match Value *</Label>
              <Input
                value={editRuleFormData.match_value}
                onChange={(e) => setEditRuleFormData({...editRuleFormData, match_value: e.target.value})}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Target Board *</Label>
              <Select
                value={editRuleFormData.target_board_id}
                onValueChange={(value) => setEditRuleFormData({...editRuleFormData, target_board_id: value})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {boards.filter(b => b.is_active).map((board) => (
                    <SelectItem key={board.id} value={board.id}>
                      {board.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Priority</Label>
              <Input
                type="number"
                min="0"
                value={editRuleFormData.priority}
                onChange={(e) => setEditRuleFormData({...editRuleFormData, priority: parseInt(e.target.value)})}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={editRuleFormData.is_active}
                onCheckedChange={(checked) => setEditRuleFormData({...editRuleFormData, is_active: checked})}
              />
              <Label>Active</Label>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditRuleDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">
                Save Changes
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View/Review Mapping Dialog */}
      <Dialog open={viewMappingDialogOpen} onOpenChange={setViewMappingDialogOpen}>
        <DialogContent className="co-scope co-settings max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Domain Mapping</DialogTitle>
          </DialogHeader>
          {selectedMapping && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Domain</p>
                  <p className="font-mono font-semibold text-lg">{selectedMapping.domain}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Proposed Customer</p>
                  <p className="font-semibold text-lg">{selectedMapping.customer_name}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  Sample Emails from this Domain ({selectedMapping.sample_emails.length})
                </p>
                <div className="space-y-1 max-h-40 overflow-y-auto bg-[#0b2130] p-3 rounded border">
                  {selectedMapping.sample_emails.map((email, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Mail className="h-3 w-3 text-gray-400" />
                      <span className="font-mono">{email}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-900">
                  <strong>What happens when you approve?</strong><br />
                  All future emails from @{selectedMapping.domain} will automatically be linked to 
                  <strong> {selectedMapping.customer_name}</strong> when creating tickets.
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    setViewMappingDialogOpen(false);
                    handleRejectMapping(selectedMapping.id);
                  }}
                  className="text-red-600"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Reject
                </Button>
                <Button
                  onClick={() => {
                    setViewMappingDialogOpen(false);
                    handleApproveMapping(selectedMapping.id);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Approve Mapping
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
